import * as Api from '@ViewModels';
import { firstNameHtml } from './richContentTokenUtils';
import { convertHtmlStringValueToPlainText } from './stringUtils';

/**
 * Creates a rich content editor state from an HTML string
 */
export const createContentStateWithHtmlStringValue = (contentHtmlStringValue: string, onResetCallback?: () => void) => {
	const rawContentState: Api.IRawRichTextContentState = {
		document: `<${Api.RawRichTextContentDocumentRootElementName}>${contentHtmlStringValue || ''}</${
			Api.RawRichTextContentDocumentRootElementName
		}>`,
		documentVersion: 1,
	};
	const content: Api.IRichContentEditorState = {
		getPlainTextPreview: () => {
			return convertHtmlStringValueToPlainText(contentHtmlStringValue);
		},
		getRawRichTextContent: () => {
			return rawContentState;
		},
		hasContent: () => {
			return !!contentHtmlStringValue;
		},
		reset: onResetCallback || Api.VmUtils.Noop,
	};
	return content;
};

/**
 * Replaces font family in CSS string
 */
export const replaceFontFamilyInCss = (fontFamily: string, targetCssStringValue: string) => {
	return (targetCssStringValue || '').replace(/font-family:[^;]+;/i, `font-family:${fontFamily},sans-serif;`);
};

/**
 * Creates a rich content editor state block (paragraph)
 */
export const createRichContentEditorStateBlock = (
	innerHtml?: string,
	defaultRawRichTextContentBlockStyleAttribute?: string
) => {
	return `<p style="${
		defaultRawRichTextContentBlockStyleAttribute || Api.DefaultRawRichTextContentBlockStyleAttribute
	}">${innerHtml || ''}</p>`;
};

const DEFAULT_PLAIN_TEXT_TRUNCATED_SIZE = 100;

/**
 * Creates a rich content editor state with text for blog posts
 */
export const createRichContentEditorStateWithTextForBlog = (text?: string, onResetCallback?: () => void) => {
	const rawFormat: Api.IRawRichTextContentState = {
		document: `<${Api.RawRichTextContentDocumentRootElementName}>${text || ''}</${Api.RawRichTextContentDocumentRootElementName}>`,
		documentVersion: 1,
	};
	const result: Api.IRichContentEditorState = {
		getPlainTextPreview: (truncate = true, terminator?: string) => {
			if (!truncate) {
				return `${text || ''}${!!text && !!terminator ? terminator : ''}`;
			}
			return `${(text || '').substring(0, DEFAULT_PLAIN_TEXT_TRUNCATED_SIZE)}${!!text && !!terminator ? terminator : ''}`;
		},
		getRawRichTextContent: () => {
			return rawFormat;
		},
		hasContent: () => {
			return !!text;
		},
		reset: onResetCallback || Api.VmUtils.Noop,
	};
	return result;
};

/**
 * Creates a rich content editor state with text
 */
export const createRichContentEditorStateWithText = (
	text?: string,
	defaultRawRichTextContentBlockStyleAttribute?: string,
	onResetCallback?: () => void
) => {
	const htmlStringValue = (text || '').split(/[\n|\r|\n\r]+/).reduce((prev, x) => {
		return `${prev}${createRichContentEditorStateBlock(x, defaultRawRichTextContentBlockStyleAttribute)}`;
	}, '');
	const rawFormat: Api.IRawRichTextContentState = {
		document: `<${Api.RawRichTextContentDocumentRootElementName}>${htmlStringValue}</${Api.RawRichTextContentDocumentRootElementName}>`,
		documentVersion: 1,
	};
	const result: Api.IRichContentEditorState = {
		getPlainTextPreview: (truncate = true, terminator?: string) => {
			if (!truncate) {
				return `${text || ''}${!!text && !!terminator ? terminator : ''}`;
			}
			return `${(text || '').substring(0, DEFAULT_PLAIN_TEXT_TRUNCATED_SIZE)}${!!text && !!terminator ? terminator : ''}`;
		},
		getRawRichTextContent: () => {
			return rawFormat;
		},
		hasContent: () => {
			return !!text;
		},
		reset: onResetCallback || Api.VmUtils.Noop,
	};
	return result;
};

/**
 * Gets default bulk messaging body editor state
 */
export const getDefaultBulkMessagingBodyEditorState = () => {
	return createContentStateWithHtmlStringValue(`Hi ${firstNameHtml},`);
};

/**
 * Creates an empty raw rich text content state
 */
export const createEmtpyRawRichTextContentState = (onResetCallback?: () => void) => {
	return createContentStateWithHtmlStringValue('', onResetCallback);
};

/**
 * Converts IRawRichTextContentState to IRichContentEditorState using `IRawRichTextContentState.content.document` as
 * input
 *
 * @param rawContentState
 */
export const convertRawRichTextContentStateToRichContentEditorState = (
	rawContentState?: Api.IRawRichTextContentState,
	plainTextContent = '',
	onResetCallback?: () => void
) => {
	const rawContentStateWithoutDraftContent = rawContentState
		? { ...rawContentState }
		: {
				document: `<${Api.RawRichTextContentDocumentRootElementName}></${Api.RawRichTextContentDocumentRootElementName}>`,
				documentVersion: 1,
			};
	// Remove draft-specific properties that shouldn't be in the final state
	// These properties exist on draft states but not on the final IRawRichTextContentState type
	const stateWithOptionalDraftProps = rawContentStateWithoutDraftContent as Record<string, unknown>;
	if ('blocks' in stateWithOptionalDraftProps) {
		delete stateWithOptionalDraftProps.blocks;
	}
	if ('entityMap' in stateWithOptionalDraftProps) {
		delete stateWithOptionalDraftProps.entityMap;
	}

	const hasContent =
		!!rawContentStateWithoutDraftContent && !!rawContentStateWithoutDraftContent.document
			? !!Api.getContentHtmlStringValueFromRawRichTextContentState(rawContentState)
			: false;

	const text =
		plainTextContent ||
		(hasContent ? convertHtmlStringValueToPlainText(rawContentStateWithoutDraftContent.document) : '');

	const result: Api.IRichContentEditorState = {
		getPlainTextPreview: (truncate = true, terminator?: string) => {
			if (!truncate) {
				return `${text || ''}${!!text && !!terminator ? terminator : ''}`;
			}
			return `${(text || '').substring(0, 100)}${!!text && !!terminator ? terminator : ''}`;
		},
		getRawRichTextContent: () => {
			return rawContentStateWithoutDraftContent;
		},
		hasContent: () => {
			return hasContent;
		},
		reset: onResetCallback || Api.VmUtils.Noop,
	};
	return result;
};
